<template>
  <div
    v-if="entries.length > 0"
    class="square-badge__container"
  >
    <template v-for="(entry, key) in entries">
      <SquareBadeCl
        :key="key"
        v-bind="entry"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { mapGetters } from 'vuex';
import { getDiscount, getPrice } from '@i22/rocket';
import { SquareBadge as SquareBadeCl } from '@i22-td-smarthome/component-library';

import type { Price } from '~/lib/goliath/goliath.types';
import type { Offer } from '~/lib/goliath/offer-by-slugs';
import type { SquareBadgeComponentProps } from '@i22-td-smarthome/component-library/dist/components/square-badge.vue';

type Badge = {
  text: string;
  footnote?: string;
  className?: string;
  color?: string;
  style?: any;
};

export default defineComponent({
  name: 'SquareBadge',
  components: { SquareBadeCl },
  props: {
    offer: {
      type: Object as PropType<Offer>,
      default: () => {},
    },
    prices: {
      type: Array as PropType<Price[]>,
      default: () => [],
    },
    text: {
      type: String,
      default: null,
    },
    footnoteText: {
      type: String,
      default: null,
    },
    customBadges: {
      type: Array as PropType<Badge[]>,
      default: () => [],
    },
    discountText: {
      type: String,
      default: 'Rabattiert',
    },
    className: {
      type: String,
      default: '',
    },
    showOnlyOne: {
      type: Boolean,
    },
  },
  computed: {
    ...mapGetters('offers', ['rebates', 'zeroPercentFinancing']),
    discount() {
      const prices = this.offer?.prices;
      if (!prices && !Array.isArray(prices)) return undefined;
      const defaultPrice = getPrice(prices, 'DEFAULT', 'ONETIME');
      const wasPrice = getPrice(prices, 'WAS', 'ONETIME');
      return defaultPrice?.amount && wasPrice?.amount
        ? getDiscount(defaultPrice.amount, wasPrice.amount)
        : undefined;
    },
    rebate() {
      return this.rebates[this.offer?.product?.articleNumber];
    },
    entries() {
      const badges: SquareBadgeComponentProps[] = [];

      if (this.customBadges?.length > 0) {
        this.customBadges.forEach((b) => {
          badges.push(b);
        });
      }

      if (badges.length > 0 && this.showOnlyOne) return badges;

      if (this.rebate?.description) {
        badges.push({
          text: this.rebate?.description,
          footnoteText: this.rebate?.footnote,
        });
        if (this.showOnlyOne) return badges;
      }
      if (this.text) {
        badges.push({
          text: this.text,
          footnoteText: this.footnoteText,
        });
        if (this.showOnlyOne) return badges;
      }
      if (this.discount) {
        badges.push({
          text: 'Aktionspreis',
          theme: 'green',
        });
        if (this.showOnlyOne) return badges;
      }
      return badges;
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.square-badge {
  display: inline-flex;
  align-items: center;
  margin: 0 0.25rem 0.5rem;
  padding: 0.688rem 1rem;

  &__container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
}
</style>
